import React, { useState, useEffect, useCallback, useRef } from 'react'
import * as XLSX from 'xlsx'
import { DataGrid, GridRowModes, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { apiUrl } from '../utils/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, TextField, Button, Tooltip, Typography, Box, MenuItem } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'

const DocumentSettings = ({
  projectId,
  access = 'none',
  deleteLocationAccess = 'none',
  deleteCategoryAccess = 'none',
  deleteStatusAccess = 'none',
  userId
}) => {
  const readOnly = access !== 'write'
  const canDeleteLocations = deleteLocationAccess === 'write'
  const canDeleteCategories = deleteCategoryAccess === 'write'
  const canDeleteStatuses = deleteStatusAccess === 'write'
  const [locationList, setLocationList] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [statusList, setStatusList] = useState([])
  const [locationRowModesModel, setLocationRowModesModel] = useState({})
  const [categoryRowModesModel, setCategoryRowModesModel] = useState({})
  const [statusRowModesModel, setStatusRowModesModel] = useState({})
  const [newLocation, setNewLocation] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [newStatus, setNewStatus] = useState('')
  const locationFileRef = useRef(null)
  const categoryFileRef = useRef(null)
  const statusFileRef = useRef(null)
  const [deletingLocs, setDeletingLocs] = useState(false)
  const [deletingCats, setDeletingCats] = useState(false)
  const [deletingStats, setDeletingStats] = useState(false)

  // Import summary state for each table
  const [locImportSummary, setLocImportSummary] = useState<any>(null)
  const [catImportSummary, setCatImportSummary] = useState<any>(null)
  const [statImportSummary, setStatImportSummary] = useState<any>(null)

  // Import source dialog state (shared — only one import at a time)
  const [importSourceOpen, setImportSourceOpen] = useState(false)
  const [pendingImport, setPendingImport] = useState<any>(null) // { file, currentList, currentField, importUrl, fetchFn, setSummary }

  // Third-party column mapping state
  const [mappingOpen, setMappingOpen] = useState(false)
  const [mapFile, setMapFile] = useState<File | null>(null)
  const [fileHeaders, setFileHeaders] = useState<string[]>([])
  const [selectedColumn, setSelectedColumn] = useState('')

  // Step 1: Import button clicked — store context and show source dialog
  const handleImportFileSelected = (currentList: any[], currentField: string, importUrl: string, fetchFn: () => void, setSummary: (s: any) => void, fileRef: React.RefObject<HTMLInputElement>) => {
    setPendingImport({ currentList, currentField, importUrl, fetchFn, setSummary, fileRef })
    setImportSourceOpen(true)
  }

  // Step 2: User picks Template or Third Party
  const handleImportSource = (isThirdParty: boolean) => {
    setImportSourceOpen(false)
    if (!pendingImport) return
    if (!isThirdParty) {
      // Template → open file picker directly
      pendingImport.fileRef.current && pendingImport.fileRef.current.click()
    } else {
      // Third Party → open mapping dialog first (file upload is inside the dialog)
      setMapFile(null)
      setFileHeaders([])
      setSelectedColumn('')
      setMappingOpen(true)
    }
  }

  // File selected in template mode
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !pendingImport) return
    runSettingsImport(file, pendingImport.currentList, pendingImport.currentField, pendingImport.importUrl, pendingImport.fetchFn, pendingImport.setSummary, null)
  }

  // File selected inside mapping dialog (third-party)
  const handleMapFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setMapFile(file)
    const reader = new FileReader()
    reader.onload = ev => {
      try {
        const data = new Uint8Array(ev.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][]
        const headers = (rows[0] || []).map(h => String(h))
        setFileHeaders(headers)
        setSelectedColumn(headers[0] || '')
      } catch { alert('Failed to read file') }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleMappingSubmit = () => {
    if (!mapFile) { alert('Please select a file'); return }
    setMappingOpen(false)
    if (!pendingImport) return
    runSettingsImport(mapFile, pendingImport.currentList, pendingImport.currentField, pendingImport.importUrl, pendingImport.fetchFn, pendingImport.setSummary, selectedColumn || null)
  }

  // Generic import processor for simple single-column settings tables
  const runSettingsImport = (
    file: File,
    currentList: any[],
    currentField: string,
    importUrl: string,
    fetchFn: () => void,
    setSummary: (s: any) => void,
    columnOverride: string | null
  ) => {
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const allRows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][]
        let colIndex = 0
        if (columnOverride) {
          const headerRow = (allRows[0] || []).map(h => String(h))
          const idx = headerRow.indexOf(columnOverride)
          colIndex = idx >= 0 ? idx : 0
        }
        const values = allRows.slice(1).map(r => r[colIndex]).filter(v => v != null && String(v).trim() !== '').map(v => String(v).trim())
        const existingSet = new Set(currentList.map(item => item[currentField] || item.name || ''))
        const existingInFile = values.filter(v => existingSet.has(v))
        const proceed = existingInFile.length === 0 || window.confirm(
          `${existingInFile.length} item(s) already exist: ${existingInFile.slice(0, 5).join(', ')}${existingInFile.length > 5 ? '...' : ''}\nProceed? Existing items will be kept (no overwrite).`
        )
        if (!proceed) return
        const fd = new FormData()
        fd.append('file', file)
        fetch(importUrl, { method: 'POST', body: fd })
          .then(res => res.json())
          .then(result => {
            const added = result.imported || 0
            const skippedNames: string[] = result.skipped || []
            const skipped = skippedNames.length
            setSummary({ added, skipped, total: values.length, values, existingInFile: skippedNames })
            fetchFn()
          })
          .catch(err => setSummary({ added: 0, skipped: 0, total: values.length, values, existingInFile: [], error: err?.message || 'Import failed' }))
      } catch { alert('Failed to read file') }
    }
    reader.readAsArrayBuffer(file)
  }


  const downloadSettingsLog = (summary: any, label: string) => {
    const logRows = summary.error
      ? (summary.values || []).map((v: string, i: number) => ({
          row_no: i + 2, value: v, status: 'Failed', notes: summary.error
        }))
      : summary.values.map((v: string, i: number) => ({
          row_no: i + 2,
          value: v,
          status: 'Pass',
          notes: summary.existingInFile.includes(v) ? 'Existing kept (skipped)' : 'New Item added'
        }))
    fetch(apiUrl('/api/document-settings/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows, label })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${label.toLowerCase()}_import_log.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  }

  const downloadFile = (url, filename) => {
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Download failed')
        return res.blob()
      })
      .then(blob => {
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
      })
      .catch(() => alert('Failed to download file'))
  }

  const fetchLocations = useCallback(() => {
    fetch(apiUrl(`/api/document-locations?project_id=${projectId}`))
      .then(res => res.json())
      .then(setLocationList)
      .catch(console.error)
  }, [projectId])

  const fetchCategories = useCallback(() => {
    fetch(apiUrl(`/api/document-categories?project_id=${projectId}`))
      .then(res => res.json())
      .then(setCategoryList)
      .catch(console.error)
  }, [projectId])

  const fetchStatuses = useCallback(() => {
    fetch(apiUrl(`/api/document-statuses?project_id=${projectId}`))
      .then(res => res.json())
      .then(setStatusList)
      .catch(console.error)
  }, [projectId])

  useEffect(() => {
    if (!projectId) { setLocationList([]); setCategoryList([]); setStatusList([]); return }
    fetchLocations()
    fetchCategories()
    fetchStatuses()
  }, [projectId, fetchLocations, fetchCategories, fetchStatuses])

  const handleAddLocation = () => {
    if (readOnly) return
    fetch(apiUrl('/api/document-locations'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_location: newLocation, project_id: projectId })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.error || 'Add failed');
          });
        }
        return res.json();
      })
      .then(() => { setNewLocation(''); fetchLocations() })
      .catch(err => alert(err.message))
  }

  const handleDeleteLocation = id => () => {
    if (readOnly) return
    if (!window.confirm('Delete this location?')) return
    fetch(apiUrl(`/api/document-locations/${id}`), { method: 'DELETE' })
      .then(res => { if (!res.ok) throw new Error('Delete failed'); fetchLocations() })
      .catch(err => alert(err.message))
  }

  const handleDeleteAllLocations = () => {
    if (!canDeleteLocations) return
    if (!window.confirm('Delete ALL locations?')) return
    setDeletingLocs(true)
    fetch(apiUrl(`/api/document-locations/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        setDeletingLocs(false)
        if (!res.ok) throw new Error('Delete all failed')
        fetchLocations()
      })
      .catch(err => {
        setDeletingLocs(false)
        alert(err.message)
      })
  }

  const handleEditLocationClick = id => () => {
    if (readOnly) return
    setLocationRowModesModel({ ...locationRowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveLocationClick = id => () => {
    if (readOnly) return
    setLocationRowModesModel({ ...locationRowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelLocationClick = id => () => {
    if (readOnly) return
    setLocationRowModesModel({
      ...locationRowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
  }

  const processLocationUpdate = updatedRow => {
    return fetch(apiUrl(`/api/document-locations/${updatedRow.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_location: updatedRow.document_location })
    })
      .then(res => { if (!res.ok) throw new Error('Update failed'); return res.json() })
      .then(() => { fetchLocations(); return updatedRow })
      .catch(err => { alert(err.message); throw err })
  }

  const handleAddCategory = () => {
    if (readOnly) return
    fetch(apiUrl('/api/document-categories'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_category: newCategory, project_id: projectId })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.error || 'Add failed');
          });
        }
        return res.json();
      })
      .then(() => { setNewCategory(''); fetchCategories() })
      .catch(err => alert(err.message))
  }

  const handleDeleteCategory = id => () => {
    if (readOnly) return
    if (!window.confirm('Delete this category?')) return
    fetch(apiUrl(`/api/document-categories/${id}`), { method: 'DELETE' })
      .then(res => { if (!res.ok) throw new Error('Delete failed'); fetchCategories() })
      .catch(err => alert(err.message))
  }

  const handleDeleteAllCategories = () => {
    if (!canDeleteCategories) return
    if (!window.confirm('Delete ALL categories?')) return
    setDeletingCats(true)
    fetch(apiUrl(`/api/document-categories/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        setDeletingCats(false)
        if (!res.ok) throw new Error('Delete all failed')
        fetchCategories()
      })
      .catch(err => {
        setDeletingCats(false)
        alert(err.message)
      })
  }

  const handleEditCategoryClick = id => () => {
    if (readOnly) return
    setCategoryRowModesModel({ ...categoryRowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveCategoryClick = id => () => {
    if (readOnly) return
    setCategoryRowModesModel({ ...categoryRowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelCategoryClick = id => () => {
    if (readOnly) return
    setCategoryRowModesModel({
      ...categoryRowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
  }

  const processCategoryUpdate = updatedRow => {
    return fetch(apiUrl(`/api/document-categories/${updatedRow.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_category: updatedRow.document_category })
    })
      .then(res => { if (!res.ok) throw new Error('Update failed'); return res.json() })
      .then(() => { fetchCategories(); return updatedRow })
      .catch(err => { alert(err.message); throw err })
  }

  const handleAddStatus = () => {
    if (readOnly) return
    fetch(apiUrl('/api/document-statuses'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_status: newStatus, project_id: projectId })
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(data => {
            throw new Error(data.error || 'Add failed');
          });
        }
        return res.json();
      })
      .then(() => { setNewStatus(''); fetchStatuses() })
      .catch(err => alert(err.message))
  }

  const handleDeleteStatus = id => () => {
    if (readOnly) return
    if (!window.confirm('Delete this status?')) return
    fetch(apiUrl(`/api/document-statuses/${id}`), { method: 'DELETE' })
      .then(res => { if (!res.ok) throw new Error('Delete failed'); fetchStatuses() })
      .catch(err => alert(err.message))
  }

  const handleDeleteAllStatuses = () => {
    if (!canDeleteStatuses) return
    if (!window.confirm('Delete ALL statuses?')) return
    setDeletingStats(true)
    fetch(apiUrl(`/api/document-statuses/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        setDeletingStats(false)
        if (!res.ok) throw new Error('Delete all failed')
        fetchStatuses()
      })
      .catch(err => {
        setDeletingStats(false)
        alert(err.message)
      })
  }

  const handleEditStatusClick = id => () => {
    if (readOnly) return
    setStatusRowModesModel({ ...statusRowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveStatusClick = id => () => {
    if (readOnly) return
    setStatusRowModesModel({ ...statusRowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelStatusClick = id => () => {
    if (readOnly) return
    setStatusRowModesModel({
      ...statusRowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
  }

  const processStatusUpdate = updatedRow => {
    return fetch(apiUrl(`/api/document-statuses/${updatedRow.id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_status: updatedRow.document_status })
    })
      .then(res => { if (!res.ok) throw new Error('Update failed'); return res.json() })
      .then(() => { fetchStatuses(); return updatedRow })
      .catch(err => { alert(err.message); throw err })
  }

  const handleDownloadLocationTemplate = () => {
    downloadFile(
      apiUrl(`/api/document-locations/template?project_id=${projectId}`),
      'document_locations_template.xlsx'
    )
  }

  const handleExportLocations = () => {
    downloadFile(
      apiUrl(`/api/document-locations/export?project_id=${projectId}`),
      'document_locations.xlsx'
    )
  }

  const handleLocationImportClick = () => {
    if (readOnly) return
    handleImportFileSelected(locationList, 'document_location', apiUrl(`/api/document-locations/import?project_id=${projectId}`), fetchLocations, setLocImportSummary, locationFileRef)
  }

  const handleDownloadCategoryTemplate = () => {
    downloadFile(
      apiUrl(`/api/document-categories/template?project_id=${projectId}`),
      'document_categories_template.xlsx'
    )
  }

  const handleExportCategories = () => {
    downloadFile(
      apiUrl(`/api/document-categories/export?project_id=${projectId}`),
      'document_categories.xlsx'
    )
  }

  const handleCategoryImportClick = () => {
    if (readOnly) return
    handleImportFileSelected(categoryList, 'document_category', apiUrl(`/api/document-categories/import?project_id=${projectId}`), fetchCategories, setCatImportSummary, categoryFileRef)
  }

  const handleDownloadStatusTemplate = () => {
    downloadFile(
      apiUrl(`/api/document-statuses/template?project_id=${projectId}`),
      'document_statuses_template.xlsx'
    )
  }

  const handleExportStatuses = () => {
    downloadFile(
      apiUrl(`/api/document-statuses/export?project_id=${projectId}`),
      'document_statuses.xlsx'
    )
  }

  const handleStatusImportClick = () => {
    if (readOnly) return
    handleImportFileSelected(statusList, 'document_status', apiUrl(`/api/document-statuses/import?project_id=${projectId}`), fetchStatuses, setStatImportSummary, statusFileRef)
  }

  const locationColumns: GridColDef[] = [
    { field: 'document_location', headerName: 'Location', flex: 1, editable: !readOnly },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: ({ id }) => {
        if (readOnly) return []
        const isEditing = locationRowModesModel[id]?.mode === GridRowModes.Edit
        return isEditing
          ? [
              <GridActionsCellItem icon={<Tooltip title="Save"><SaveIcon /></Tooltip>} label="Save" onClick={handleSaveLocationClick(id)} showInMenu={false} key="save" />,
              <GridActionsCellItem icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>} label="Cancel" onClick={handleCancelLocationClick(id)} showInMenu={false} key="cancel" />
            ]
          : [
              <GridActionsCellItem icon={<Tooltip title="Edit"><EditIcon /></Tooltip>} label="Edit" onClick={handleEditLocationClick(id)} showInMenu={false} key="edit" />,
              <GridActionsCellItem icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>} label="Delete" onClick={handleDeleteLocation(id)} showInMenu={false} key="delete" />
            ]
      }
    }
  ]

  const categoryColumns: GridColDef[] = [
    { field: 'document_category', headerName: 'Category', flex: 1, editable: !readOnly },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: ({ id }) => {
        if (readOnly) return []
        const isEditing = categoryRowModesModel[id]?.mode === GridRowModes.Edit
        return isEditing
          ? [
              <GridActionsCellItem icon={<Tooltip title="Save"><SaveIcon /></Tooltip>} label="Save" onClick={handleSaveCategoryClick(id)} showInMenu={false} key="save" />,
              <GridActionsCellItem icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>} label="Cancel" onClick={handleCancelCategoryClick(id)} showInMenu={false} key="cancel" />
            ]
          : [
              <GridActionsCellItem icon={<Tooltip title="Edit"><EditIcon /></Tooltip>} label="Edit" onClick={handleEditCategoryClick(id)} showInMenu={false} key="edit" />,
              <GridActionsCellItem icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>} label="Delete" onClick={handleDeleteCategory(id)} showInMenu={false} key="delete" />
            ]
      }
    }
  ]

  const statusColumns: GridColDef[] = [
    { field: 'document_status', headerName: 'Status', flex: 1, editable: !readOnly },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: ({ id }) => {
        if (readOnly) return []
        const isEditing = statusRowModesModel[id]?.mode === GridRowModes.Edit
        return isEditing
          ? [
              <GridActionsCellItem icon={<Tooltip title="Save"><SaveIcon /></Tooltip>} label="Save" onClick={handleSaveStatusClick(id)} showInMenu={false} key="save" />,
              <GridActionsCellItem icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>} label="Cancel" onClick={handleCancelStatusClick(id)} showInMenu={false} key="cancel" />
            ]
          : [
              <GridActionsCellItem icon={<Tooltip title="Edit"><EditIcon /></Tooltip>} label="Edit" onClick={handleEditStatusClick(id)} showInMenu={false} key="edit" />,
              <GridActionsCellItem icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>} label="Delete" onClick={handleDeleteStatus(id)} showInMenu={false} key="delete" />
            ]
      }
    }
  ]

  if (access === 'none') return <div>Access denied</div>
  if (!projectId) return <div>Please select a project.</div>

  return (
    <>
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: 1 }}>
          <h3>Document Locations</h3>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <TextField label="New Location" size="small" value={newLocation} onChange={e => setNewLocation(e.target.value)} />
            {!readOnly && <Button variant="contained" onClick={handleAddLocation}>Add</Button>}
            {canDeleteLocations && (
              <Button variant="contained" color="error" onClick={handleDeleteAllLocations}>
                Delete All
              </Button>
            )}
          </div>
          <div style={{ height: 300 }}>
            <DataGrid
              rows={locationList}
              columns={locationColumns}
              getRowId={row => row.id}
              editMode={readOnly ? undefined : 'row'}
              rowModesModel={locationRowModesModel}
              onRowModesModelChange={setLocationRowModesModel}
              processRowUpdate={readOnly ? undefined : processLocationUpdate}
              hideFooter
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <Button variant="outlined" onClick={handleDownloadLocationTemplate} style={{ marginRight: 8 }}>
              Export Template
            </Button>
            <Button variant="outlined" onClick={handleExportLocations} style={{ marginRight: 8 }}>
              Export Table
            </Button>
            <Button variant="contained" onClick={handleLocationImportClick} disabled={readOnly}>
              Import
            </Button>
            <input
              type="file"
              accept=".xlsx"
              ref={locationFileRef}
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <h3>Document Categories</h3>
          <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
            <TextField label="New Category" size="small" value={newCategory} onChange={e => setNewCategory(e.target.value)} />
            {!readOnly && <Button variant="contained" onClick={handleAddCategory}>Add</Button>}
            {canDeleteCategories && (
              <Button variant="contained" color="error" onClick={handleDeleteAllCategories}>
                Delete All
              </Button>
            )}
          </div>
          <div style={{ height: 300 }}>
            <DataGrid
              rows={categoryList}
              columns={categoryColumns}
              getRowId={row => row.id}
              editMode={readOnly ? undefined : 'row'}
              rowModesModel={categoryRowModesModel}
              onRowModesModelChange={setCategoryRowModesModel}
              processRowUpdate={readOnly ? undefined : processCategoryUpdate}
              hideFooter
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <Button variant="outlined" onClick={handleDownloadCategoryTemplate} style={{ marginRight: 8 }}>
              Export Template
            </Button>
            <Button variant="outlined" onClick={handleExportCategories} style={{ marginRight: 8 }}>
              Export Table
            </Button>
            <Button variant="contained" onClick={handleCategoryImportClick} disabled={readOnly}>
              Import
            </Button>
            <input
              type="file"
              accept=".xlsx"
              ref={categoryFileRef}
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 32 }}>
        <h3>Document Statuses</h3>
        <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
          <TextField label="New Status" size="small" value={newStatus} onChange={e => setNewStatus(e.target.value)} />
          {!readOnly && <Button variant="contained" onClick={handleAddStatus}>Add</Button>}
          {canDeleteStatuses && (
            <Button variant="contained" color="error" onClick={handleDeleteAllStatuses}>
              Delete All
            </Button>
          )}
        </div>
        <div style={{ height: 300 }}>
          <DataGrid
            rows={statusList}
            columns={statusColumns}
            getRowId={row => row.id}
            editMode={readOnly ? undefined : 'row'}
            rowModesModel={statusRowModesModel}
            onRowModesModelChange={setStatusRowModesModel}
            processRowUpdate={readOnly ? undefined : processStatusUpdate}
            hideFooter
          />
        </div>
        <div style={{ marginTop: 8 }}>
          <Button variant="outlined" onClick={handleDownloadStatusTemplate} style={{ marginRight: 8 }}>
            Export Template
          </Button>
          <Button variant="outlined" onClick={handleExportStatuses} style={{ marginRight: 8 }}>
            Export Table
          </Button>
          <Button variant="contained" onClick={handleStatusImportClick} disabled={readOnly}>
            Import
          </Button>
          <input
            type="file"
            accept=".xlsx"
            ref={statusFileRef}
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
    <Dialog open={deletingLocs}>
      <DialogTitle>Deleting...</DialogTitle>
      <DialogContent sx={{ minWidth: 300 }}><LinearProgress /></DialogContent>
    </Dialog>
    <Dialog open={deletingCats}>
      <DialogTitle>Deleting...</DialogTitle>
      <DialogContent sx={{ minWidth: 300 }}><LinearProgress /></DialogContent>
    </Dialog>
    <Dialog open={deletingStats}>
      <DialogTitle>Deleting...</DialogTitle>
      <DialogContent sx={{ minWidth: 300 }}><LinearProgress /></DialogContent>
    </Dialog>

    {/* Import Source Dialog */}
    <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
      <DialogTitle>Import Source</DialogTitle>
      <DialogContent>
        <Typography>Is this Excel file from a third-party source?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleImportSource(false)}>Template</Button>
        <Button variant="contained" onClick={() => handleImportSource(true)}>Third Party</Button>
      </DialogActions>
    </Dialog>

    {/* Third-Party Column Mapping Dialog */}
    <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Column Mapping</DialogTitle>
      <DialogContent>
        <input type="file" accept=".xlsx" onChange={handleMapFileChange} style={{ marginBottom: 16 }} />
        {fileHeaders.length > 0 && (
          <TextField
            select
            label={pendingImport?.currentField === 'document_location' ? 'Location' : pendingImport?.currentField === 'document_category' ? 'Category' : 'Status'}
            value={selectedColumn}
            onChange={e => setSelectedColumn(e.target.value)}
            fullWidth
            margin="dense"
          >
            {fileHeaders.map(h => (
              <MenuItem key={h} value={h}>{h}</MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleMappingSubmit}>Import</Button>
      </DialogActions>
    </Dialog>

    {/* Import Summary Dialogs */}
    {[
      { summary: locImportSummary, setSummary: setLocImportSummary, label: 'Location' },
      { summary: catImportSummary, setSummary: setCatImportSummary, label: 'Category' },
      { summary: statImportSummary, setSummary: setStatImportSummary, label: 'Status' },
    ].map(({ summary, setSummary, label }) =>
      summary ? (
        <Dialog key={label} open={true} onClose={() => setSummary(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{summary.error ? `Import Failed — ${label}` : `Import Complete — ${label}`}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Total rows in file:</Typography>
                <Typography fontWeight={600}>{summary.total}</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="success.main">New items added:</Typography>
                <Typography fontWeight={600} color="success.main">{summary.added}</Typography>
              </div>
              {summary.skipped > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="text.secondary">Already existed (skipped):</Typography>
                  <Typography fontWeight={600} color="text.secondary">{summary.skipped}</Typography>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: 8 }}>
                <Typography fontWeight={600}>Total changes:</Typography>
                <Typography fontWeight={600}>{summary.added}</Typography>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => downloadSettingsLog(summary, label)}>Download Import Log</Button>
            <Button variant="contained" onClick={() => setSummary(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      ) : null
    )}
    </>
  )
}

export default DocumentSettings
